import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Alert, Typography, Box, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  unsubscribeLeadContact, resubscribeLeadContact,
  markLeadContactInvalid, unmarkLeadContactInvalid,
  deleteLeadContact,
} from '../services/api';

const CONFIG = {
  unsubscribe: {
    title: 'Unsubscribe contact?',
    body: 'This contact will be suppressed from all outreach. Pending drafts, scheduled and ready-to-send emails will be cancelled.',
    needsReason: true,
    confirmLabel: 'Unsubscribe',
    confirmColor: 'warning',
  },
  resubscribe: {
    title: 'Resubscribe contact?',
    body: 'Re-enable outreach to this contact.',
    needsReason: false,
    confirmLabel: 'Resubscribe',
    confirmColor: 'primary',
  },
  invalid: {
    title: 'Mark contact as invalid?',
    body: 'Contact will be treated as unusable (bad data, ceased role, etc.). Blocks all future outreach and cancels pending sends.',
    needsReason: true,
    confirmLabel: 'Mark invalid',
    confirmColor: 'warning',
  },
  revalidate: {
    title: 'Clear invalid flag?',
    body: 'Restore this contact to outreach-eligible.',
    needsReason: false,
    confirmLabel: 'Clear flag',
    confirmColor: 'primary',
  },
  delete: {
    title: 'Delete contact?',
    body: 'This is permanent. The contact will be removed but past interactions remain (orphan contact reference).',
    needsReason: false,
    confirmLabel: 'Delete',
    confirmColor: 'error',
    requiresNameMatch: true,
  },
};

export default function ContactStatusDialog({ open, leadId, contact, action, onClose, onChanged }) {
  const [reason, setReason] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
      setConfirmName('');
      setError('');
    }
  }, [open]);

  if (!action || !contact) return null;

  const cfg = CONFIG[action];
  if (!cfg) return null;

  const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || '(no name)';
  const nameMismatch = cfg.requiresNameMatch && confirmName.trim() !== fullName;

  const handleConfirm = async () => {
    setWorking(true);
    setError('');
    try {
      const id = contact.id;
      if (action === 'unsubscribe') await unsubscribeLeadContact(leadId, id, reason);
      else if (action === 'resubscribe') await resubscribeLeadContact(leadId, id);
      else if (action === 'invalid') await markLeadContactInvalid(leadId, id, reason);
      else if (action === 'revalidate') await unmarkLeadContactInvalid(leadId, id);
      else if (action === 'delete') await deleteLeadContact(leadId, id);
      onChanged?.(action);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data || 'Action failed');
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onClose={working ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {action === 'delete' && <WarningAmberIcon color="error" />}
          {cfg.title}
        </Box>
        <IconButton onClick={onClose} disabled={working} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>{cfg.body}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Contact: <strong>{fullName}</strong>{contact.email ? ` · ${contact.email}` : ''}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{String(error)}</Alert>}
        {cfg.needsReason && (
          <TextField
            label="Reason (optional)"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={action === 'invalid' ? 'e.g. left company, wrong department, bad data' : 'e.g. requested removal'}
          />
        )}
        {cfg.requiresNameMatch && (
          <>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, mt: 1 }}>
              Type the full name <strong>{fullName}</strong> to confirm:
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              error={!!confirmName && nameMismatch}
              helperText={confirmName && nameMismatch ? 'Name does not match' : ' '}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={working}>Cancel</Button>
        <Button
          variant="contained"
          color={cfg.confirmColor}
          disabled={working || nameMismatch}
          onClick={handleConfirm}
        >
          {working ? 'Working…' : cfg.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
